import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
  Collapse,
  IconButton,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import CopyableTypography from "components/Typography/CopyableTypography";
import { ALL_CA_SECTION_IDS, compareSectionIds } from "encamp-shared/src/hmbp/sections";
import { ActivityType } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useFulfillmentDetails } from "../useFulfillmentDetails";
import { getRelevantPortalCredential } from "../useRelevantPortalCredential";
import { buildGeppettoCommand, getGeppettoAction } from "./geppettoCommandUtils";
import { SectionSelector } from "./SectionSelector";

const GeppettoCommand = () => {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();
  const { activityId } = useParams();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-geppetto-command-expanded",
    { defaultValue: true }
  );

  const viewingActivity = data?.tierIIReport.currentWorkflow?.activities.find(
    (a) => a.id === activityId
  );

  const isViewingGeppettoActivity =
    viewingActivity?.type === ActivityType.Geppetto ||
    viewingActivity?.type === ActivityType.CollectEvidence;

  const action = getGeppettoAction(
    viewingActivity?.type,
    viewingActivity?.currentJob?.action,
    viewingActivity?.description,
    data?.tierIIReport.reportKind
  );

  const facilityId = data?.tierIIReport.facility.id;

  const reportKind = data?.tierIIReport.reportKind;

  const jobId = viewingActivity?.currentJob?.id;

  const relevantCredential = getRelevantPortalCredential(
    data?.tierIIReport.facility?.portalCredentials,
    viewingActivity
  );
  const credentialId = relevantCredential?.credential?.id;

  const reportId = data?.tierIIReport.id;

  const isCAReport = data?.tierIIReport.facility?.state === "CA";

  const persistedJobParameters = viewingActivity?.currentJob?.parameters;

  const persistedSectionIds = useMemo<string[] | null>(() => {
    if (!isCAReport) return null;
    if (typeof persistedJobParameters !== "object" || persistedJobParameters === null) return null;
    const ids = (persistedJobParameters as { sectionIds?: unknown }).sectionIds;
    if (!Array.isArray(ids)) return null;
    const stringIds = ids.filter((id): id is string => typeof id === "string");
    return [...stringIds].sort(compareSectionIds);
  }, [isCAReport, persistedJobParameters]);

  const persistedSource = useMemo(() => {
    if (typeof persistedJobParameters !== "object" || persistedJobParameters === null)
      return undefined;
    const source = (persistedJobParameters as { sectionSelectionSource?: unknown })
      .sectionSelectionSource;
    if (
      source === "default" ||
      source === "user" ||
      source === "auto-detect" ||
      source === "auto-detect-fallback"
    ) {
      return source;
    }
    return undefined;
  }, [persistedJobParameters]);

  const persistedFallbackReason = useMemo(() => {
    if (typeof persistedJobParameters !== "object" || persistedJobParameters === null)
      return undefined;
    const reason = (persistedJobParameters as { autoDetectFallbackReason?: unknown })
      .autoDetectFallbackReason;
    if (
      reason === "no-prior-snapshot" ||
      reason === "version-mismatch" ||
      reason === "snapshot-load-failure" ||
      reason === "compute-error"
    ) {
      return reason;
    }
    return undefined;
  }, [persistedJobParameters]);

  const baselineSectionIds = useMemo<string[]>(() => {
    if (!isCAReport) return [];
    return persistedSectionIds ?? [...ALL_CA_SECTION_IDS];
  }, [isCAReport, persistedSectionIds]);

  const [sectionIds, setSectionIds] = useState<string[]>(baselineSectionIds);

  // The baseline (and the user's "untouched" reference) should reset only when
  // the underlying activity/job actually changes — not on every Apollo refetch
  // that returns identical data, which would discard a mid-edit selection.
  // Key on activity + job ids + CA-ness + the persisted section-id set so a
  // genuine swap (different activity, freshly created job, or a job whose
  // persisted sectionIds were updated server-side) resets, but a benign refetch
  // does not.
  const baselineKey = `${activityId ?? ""}|${viewingActivity?.currentJob?.id ?? ""}|${
    isCAReport ? "CA" : "non-CA"
  }|${persistedSectionIds ? persistedSectionIds.join(",") : "none"}`;
  const baselineKeyRef = useRef(baselineKey);
  const initialSectionIdsRef = useRef<string[]>(baselineSectionIds);
  useEffect(() => {
    if (baselineKeyRef.current !== baselineKey) {
      baselineKeyRef.current = baselineKey;
      initialSectionIdsRef.current = [...baselineSectionIds];
      setSectionIds(baselineSectionIds);
    }
  }, [baselineKey, baselineSectionIds]);

  const allDefaultsSelected =
    sectionIds.length === baselineSectionIds.length &&
    baselineSectionIds.every((id) => sectionIds.includes(id));

  // Omit `--sections` from the generated command only when the local selection
  // matches the baseline AND there is no persisted, narrowed selection on the
  // job. If the persisted job already has a smaller `sectionIds` set (e.g. a
  // REVISION default of `["1"]`), passing the command without `--sections`
  // would be a no-op for `--job-id` (the persisted set wins) but misleading
  // for `--create-job`. Always emit the explicit set when persistence has
  // narrowed the default.
  const sectionIdsForCommand =
    !isCAReport || (allDefaultsSelected && persistedSectionIds === null) ? undefined : sectionIds;

  const userHasModified = useMemo(() => {
    const initial = initialSectionIdsRef.current;
    if (sectionIds.length !== initial.length) return true;
    const a = [...sectionIds].sort(compareSectionIds);
    const b = [...initial].sort(compareSectionIds);
    return a.some((id, i) => id !== b[i]);
  }, [sectionIds]);

  const badgeLabel: string | null = (() => {
    if (!isCAReport) return null;
    if (userHasModified || persistedSource === "user") return "Overridden by staff";
    if (persistedSource === "default") return "Default applied";
    if (persistedSource === "auto-detect") return "Auto-detected";
    if (persistedSource === "auto-detect-fallback") {
      // Key the fallback copy off the reason so a transient `compute-error`
      // or `version-mismatch` isn't misrepresented as a fresh-report scenario.
      switch (persistedFallbackReason) {
        case "no-prior-snapshot":
          return "Auto-detect: no prior submission, defaulted";
        case "version-mismatch":
          return "Auto-detect: snapshot schema mismatch, defaulted";
        case "snapshot-load-failure":
          return "Auto-detect: snapshot unreadable, defaulted";
        case "compute-error":
          return "Auto-detect: compute failure, defaulted";
        default:
          return "Auto-detect: fell back to defaults";
      }
    }
    return null;
  })();

  const badgeColor: "default" | "warning" | "info" = (() => {
    if (badgeLabel === null) return "default";
    if (badgeLabel === "Default applied") return "default";
    if (badgeLabel === "Auto-detected") return "info";
    return "warning";
  })();

  const geppettoCommand = useMemo(() => {
    return buildGeppettoCommand({
      action,
      reportingYear: data?.tierIIReport.reportingYear,
      reportKind,
      facilityId,
      jobId,
      credentialId,
      reportId,
      sectionIds: sectionIdsForCommand,
    });
  }, [
    action,
    facilityId,
    reportKind,
    jobId,
    credentialId,
    reportId,
    data?.tierIIReport.reportingYear,
    sectionIdsForCommand,
  ]);

  if (!isViewingGeppettoActivity) return <></>;

  return (
    <Stack direction={"column"} component={Paper} elevation={0} sx={{ padding: theme.spacing(3) }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6">Geppetto Command</Typography>
        <IconButton size="small" sx={{ ml: 1 }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ mt: 3 }}>
          {isCAReport && (
            <>
              {badgeLabel && (
                <Chip label={badgeLabel} size="small" color={badgeColor} sx={{ mb: 1 }} />
              )}
              <SectionSelector value={sectionIds} onChange={setSectionIds} />
              <Divider sx={{ my: 2 }} />
            </>
          )}
          <CopyableTypography text={geppettoCommand} />
        </Box>
      </Collapse>
    </Stack>
  );
};

export { GeppettoCommand };
